import { useQuery } from '@tanstack/react-query';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import { createSession } from '@/api/sessions';
import { HttpError } from '@/api/client';
import { ErrorScreen, InvalidCodeScreen, LoadingScreen } from '@/features/check/StatusScreen';
import { readsAsCardOnly, setCardOnly } from '@/payment/cardOnly';
import { saveSession, toStoredSession } from '@/session/storage';

/**
 * Where a guest lands from the QR code: `/c/{shortCode}`.
 *
 * Exchanges the code for a session, stores it, and moves on. The code itself is never kept — it is a bearer
 * credential printed on a slip of paper, and once traded for a scoped token it has no further use.
 *
 * A query rather than a mutation, despite the POST. The exchange is a read as far as this app is concerned:
 * it resolves a code into a session and changes nothing a second call would disturb. Modelling it as a query
 * gets deduplication and a cache for free — and avoids the trap of firing a mutation from an effect, where
 * React's development double-mount discards the first result and any guard against re-firing leaves the
 * screen stuck loading forever.
 */
export function ScanRoute() {
    const { code } = useParams<{ code: string }>();
    const { search } = useLocation();

    const session = useQuery({
        queryKey: ['session', code],
        queryFn: async ({ signal }) => {
            const created = await createSession(code!, signal);

            /*
             * Persisted here rather than in an effect so it is on disk *before* this query reports success.
             * CheckRoute reads storage on mount, so redirecting first and saving after is a race the guest
             * loses as a spurious "scan again" screen while holding a session that was just created.
             */
            saveSession(toStoredSession(created));

            /*
             * The only moment `?cardOnly=true` is ever in the URL. The redirect below drops the query string,
             * and four navigations plus a hosted payment page stand between here and the method choice, so
             * the answer is written down beside the session it belongs to.
             *
             * Set either way, never only when true: the tablet's WebView opens a different check tomorrow on
             * the same origin, and a stale `true` would take a later guest's wallets away.
             */
            setCardOnly(readsAsCardOnly(search));

            return created;
        },
        enabled: !!code,
        // The token is already stored; re-running this on a remount would burn the guest's rate-limit budget
        // on the one endpoint that gates everything else.
        staleTime: Infinity,
        gcTime: Infinity,
        // A bad code answers the same 404 every time. Retrying only delays telling the guest.
        retry: false,
    });

    if (!code) return <InvalidCodeScreen />;

    if (session.isSuccess) {
        // Replace rather than push, so the code leaves the history entry as well as the address bar. Guests
        // screenshot and share these pages, and Back should not walk them into a spent code.
        return <Navigate to="/check" replace />;
    }

    if (session.isError) {
        const status = session.error instanceof HttpError ? session.error.status : undefined;

        // 404 is every "this code will not open a check" case, deliberately indistinguishable.
        if (status === 404) return <InvalidCodeScreen />;

        if (status === 429) {
            return (
                <ErrorScreen
                    titleKey="error.rateLimited.title"
                    detailKey="error.rateLimited.detail"
                    />
            );
        }

        return (
            <ErrorScreen
                titleKey="error.unreachable.title"
                detailKey="error.unreachable.detail"
                onRetry={() => session.refetch()}
            />
        );
    }

    return <LoadingScreen />;
}
